const THEME_BASE_URL = new URL('../../theme-tokens/', import.meta.url);
const DEFAULT_THEME = 'light-mode';

const FALLBACK_OPTIONS = [
  { id: 'light-mode', label: 'Retro Light' },
  { id: 'dark-mode', label: 'Retro Dark' },
  { id: 'boring-light', label: 'Boring Mode' },
  { id: 'boring-dark', label: 'Boring Night' }
];

let themeOptionsPromise = null;
const themePromises = new Map();
const labels = new Map(FALLBACK_OPTIONS.map((item) => [item.id, item.label]));

function themeUrl(file) {
  return new URL(file, THEME_BASE_URL).toString();
}

function normalizeId(value) {
  const id = String(value || '').trim();
  return /^[a-z0-9-]+$/.test(id) ? id : '';
}

async function fetchJson(url, label) {
  const response = await fetch(url, { headers: { accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`${label} request failed with ${response.status}`);
  }
  return response.json();
}

function normalizeOptions(value) {
  if (!Array.isArray(value)) return FALLBACK_OPTIONS;
  const options = value
    .map((item) => ({
      id: normalizeId(item?.id),
      label: String(item?.label || item?.id || '').trim()
    }))
    .filter((item) => item.id && item.label);
  const normalized = options.length ? options : FALLBACK_OPTIONS;
  normalized.forEach((item) => labels.set(item.id, item.label));
  return normalized;
}

function normalizeTokens(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .filter(([name, token]) => /^--[a-zA-Z0-9_-]+$/.test(name) && token !== null && token !== undefined)
    .map(([name, token]) => [name, String(token)]));
}

function normalizeTheme(value, requestedId) {
  if (!value || typeof value !== 'object') return null;
  const id = normalizeId(value.id) || requestedId;
  if (id !== requestedId) return null;
  const label = String(value.label || labels.get(id) || id);
  const tokens = normalizeTokens(value.tokens);
  if (!Object.keys(tokens).length) return null;
  labels.set(id, label);
  return { id, label, tokens };
}

function tokensToCss(tokens) {
  const lines = Object.entries(tokens).map(([name, value]) => `  ${name}: ${value};`);
  return `:root {\n${lines.join('\n')}\n}\n`;
}

function applyTokens(tokens) {
  const style = document.documentElement.style;
  Object.entries(tokens).forEach(([name, value]) => {
    style.setProperty(name, value);
  });
}

export async function getThemeOptions() {
  if (!themeOptionsPromise) {
    themeOptionsPromise = fetchJson(themeUrl('manifest.json'), 'Theme manifest')
      .then(normalizeOptions)
      .catch((err) => {
        console.error('Failed to load theme manifest:', err);
        return FALLBACK_OPTIONS;
      });
  }
  return themeOptionsPromise;
}

export function themeLabel(theme) {
  return labels.get(theme) || theme;
}

export async function loadThemeTokens(theme) {
  const id = normalizeId(theme) || DEFAULT_THEME;

  if (!themePromises.has(id)) {
    themePromises.set(id, fetchJson(themeUrl(`${id}.json`), `Theme ${id}`)
      .then((payload) => normalizeTheme(payload, id))
      .catch(async (err) => {
        themePromises.delete(id);
        console.error(`Failed to load theme tokens '${id}':`, err);
        if (id !== DEFAULT_THEME) return loadThemeTokens(DEFAULT_THEME);
        throw err;
      }));
  }

  return themePromises.get(id);
}

export function applyLoadedThemeTokens(payload) {
  applyTokens(payload.tokens);
  return payload;
}

export async function applyThemeTokens(theme) {
  const payload = await loadThemeTokens(theme);
  return applyLoadedThemeTokens(payload);
}

export async function getPreviewThemeCss(theme) {
  const payload = await loadThemeTokens(theme);
  return tokensToCss(payload.tokens);
}
